// src/pages/account/Subscription.tsx
import React, { useEffect, useState } from "react";
import { Button, Callout, Spinner } from "@blueprintjs/core";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useAuth } from "../../auth/AuthContext";
import { API_BASE } from "../../utils/apiBase";

const STRIPE_PUB =
  "pk_live_51PLwHiIvUtV48mNeeHEXjLsi9W39CKQl9HuCfCD52XJ8Ua84MkFM8iKlEdNs33wZPRftLTCH6lxPCE8W8BifXFHX00I9724nkf";

export {}; // force module scope for global declarations

declare global {
  interface Window {
    idToken?: string;
  }
}

export default function Subscription() {
  const { tier, idToken, loading: authLoading } = useAuth();
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    loadStripe(STRIPE_PUB).then(setStripe);
  }, [setStripe]);

  useEffect(() => {
    if (idToken) {
      window.idToken = idToken; // for curl/debug use only
      console.log("[debug] idToken exposed on window");
    }
  }, [idToken]);

  const isPro = tier === "pro";
  const buttonText = isPro
    ? "Manage subscription"
    : tier === "enterprise"
    ? "Contact us"
    : "Subscribe";
  const buttonIntent = isPro ? "primary" : "success";
  const buttonDisabled = !stripe || tier === "enterprise";

  /* ---------- Action ---------- */
  const handleClick = async () => {
    if (!stripe) return;

    const pro = tier === "pro";
    const endpoint = pro
      ? `${API_BASE}/create-portal-session`
      : `${API_BASE}/create-checkout-session`;

    console.log("[subscription] hitting:", endpoint);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
        },
        body: "{}", // POST even if empty
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("[subscription] failed fetch:", res.status, text);
        return;
      }

      const data = await res.json();
      console.log("[subscription] response:", data);

      if (pro) {
        if (!data.url) {
          console.error("[portal] no URL returned from server");
          return;
        }
        window.location.href = data.url;
      } else {
        if (!data.id) {
          console.error("[checkout] no session ID returned");
          return;
        }
        const result = await stripe.redirectToCheckout({ sessionId: data.id });
        if (result.error) {
          console.error("[checkout] redirect error:", result.error.message);
        }
      }
    } catch (err) {
      console.error("[subscription] exception thrown:", err);
    }
  };

  /* ---------- UI ---------- */
  if (authLoading) {
    return (
      <div style={{ textAlign: "center", padding: "4rem" }}>
        <Spinner intent="primary" size={40} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "4rem" }}>
      <Callout
        title="Subscription status"
        intent={isPro ? "success" : "warning"}
        style={{ marginBottom: "2rem", maxWidth: 600, marginInline: "auto" }}
      >
        {tier === "free" && (
          <p>
            You’re on the <strong>free</strong> plan. Upgrade to unlock pro
            features.
          </p>
        )}
        {isPro && (
          <p>
            You have an active <strong>Pro</strong> subscription. You can update
            billing details or cancel anytime.
          </p>
        )}
        {tier === "enterprise" && (
          <p>
            You’re on an <strong>Enterprise</strong> agreement. Contact support
            for billing questions.
          </p>
        )}
      </Callout>

      <Button
        text={buttonText}
        intent={buttonIntent}
        large
        onClick={handleClick}
        disabled={buttonDisabled}
      />
    </div>
  );
}
